import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Waypoint } from 'react-waypoint';

import Container from '../components/container';
import Footer from '../components/footer';
import NavBar from '../components/nav_bar';
import NoticeBanner from '../components/notice_banner';
import { NavigationItemProps, NoticeProps } from '../types';

import styles from './style.module.scss';

interface Props {
  children: any;
  childrenStyle?: any;
  darkMode?: boolean;
  dashedBorder?: boolean;
  examName: string;
  navigationItems: {
    items: NavigationItemProps[];
    supplementalItems: NavigationItemProps[];
  };
  navigationStyle?: any;
  notice?: NoticeProps;
}

const PageLayout: React.FC<Props> = (props) => {
  const navbarRef = useRef<HTMLDivElement>(null);
  const waypointRef = useRef(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [navbarHeight, setNavbarHeight] = useState(0);

  const determineNavbarHeight = useRef(() => {
    setNavbarHeight(navbarRef.current.clientHeight);
  });

  useEffect(() => {
    window.addEventListener('resize', () => {
      determineNavbarHeight.current();
    });
  });

  useEffect(() => {
    if (navbarRef.current) {
      determineNavbarHeight.current();
    }
  }, []);

  // If page is reloaded and the user is past the waypoint it won't trigger.
  // Do a one time check if we are already past the waypoint and show the sticky bar if so.
  useEffect(() => {
    const rect = waypointRef?.current?.getBoundingClientRect();
    if (!rect) return;

    if (rect.y < 0 || window.location.hash) setIsSticky(true);
  }, []);

  return (
    <div className="d-flex flex-column" style={{ minHeight: '100vh' }}>
      <Container className="flex-1" dashedBorder={props.dashedBorder}>
        <div
          className={cx(styles.navbarContainer, { [styles.sticky]: isSticky, [styles.darkMode]: props.darkMode })}
          ref={navbarRef}
          style={{ zIndex: isSticky ? 20 : 15, ...(props.navigationStyle || {}) }}
        >
          <div>
            {
              props.notice && (
                <div className={cx(styles.stickyNoticeBannerContainer, { [styles.sticky]: isSticky })}>
                  <Container>
                    <NoticeBanner {...props.notice} role="sale" />
                  </Container>
                </div>
              )
            }

            <Container>
              <NavBar
                darkMode={props.darkMode}
                isSticky={isSticky}
                navigationItems={props.navigationItems}
              />
            </Container>
          </div>
        </div>

        <div
          ref={waypointRef}
          style={{ height: 1, left: 0, position: 'absolute', right: 0, top: 500 }}
        >
          <Waypoint
            onEnter={() => setIsSticky(false)}
            onLeave={() => setIsSticky(true)}
          />
        </div>

        {
          props.notice && (
            <div className={styles.noticeBannerContainer}>
              <NoticeBanner {...props.notice} role="sale" />
            </div>
          )
        }

        <div
          className="d-flex flex-column"
           id="resizeable-container"
          style={{
            gap: 80,
            marginTop: isSticky ? navbarHeight : 0,
            position: 'relative',
            zIndex: 10,
            ...(props.childrenStyle || {})
          }}
        >
          {
            typeof props.children === 'string' ? (
              <div dangerouslySetInnerHTML={{ __html: props.children }} />
            ) : props.children
          }
        </div>
      </Container>

      <div className="u-margin-T-xl">
        <Footer examName={props.examName} />
      </div>
    </div>
  );
};

export default PageLayout;
